import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { site, pricing, photos } from "../data/siteConfig";

const line = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: {
            delay: 0.35 + i * 0.14,
            duration: 1.1,
            ease: [0.22, 1, 0.36, 1],
        },
    }),
};

const MaskedLine = ({ index, children, className = "" }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className={`block ${className}`}
            custom={index}
            variants={line}
            initial="hidden"
            animate="show"
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => (
    <section
        id="top"
        className="relative flex min-h-[100svh] items-center overflow-hidden text-[#1c1917]"
        data-testid="hero-section"
    >
        {/* Warm photographic background */}
        <div className="absolute inset-0">
            <img
                src={photos.heroBg.src}
                alt={photos.heroBg.alt}
                className="h-full w-full object-cover object-center lg:object-[center_25%]"
                fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/80 to-cream/15" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-cream/90 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-32 sm:px-8">
            <motion.p
                className="font-mono text-[11px] uppercase tracking-[0.35em] text-stone-600 sm:text-xs"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.8 }}
                data-testid="hero-eyebrow"
            >
                {site.locationLine}
            </motion.p>

            <h1
                className="mt-4 font-serif font-light leading-[1.04] tracking-tight"
                data-testid="hero-headline"
            >
                <MaskedLine index={0} className="text-6xl sm:text-8xl lg:text-9xl">
                    Mākara <em className="font-medium">Cattery</em>
                </MaskedLine>
                <MaskedLine
                    index={1}
                    className="mt-3 font-serif text-2xl italic text-stone-700 sm:text-4xl"
                >
                    “A country holiday for your cat”
                </MaskedLine>
            </h1>

            <motion.div
                className="mt-7 max-w-xl space-y-4 text-base leading-relaxed text-stone-700 sm:text-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.95, duration: 0.8 }}
                data-testid="hero-subline"
            >
                {site.heroSub.split("\n\n").map((p) => (
                    <p key={p.slice(0, 32)}>{p}</p>
                ))}
            </motion.div>

            <motion.div
                className="mt-10 flex flex-wrap items-center gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.8 }}
            >
                <a
                    href="#book"
                    data-testid="hero-book-button"
                    className="rounded-full bg-[#1c1917] px-8 py-4 text-base font-semibold text-cream shadow-xl shadow-black/15 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-black sm:text-lg"
                >
                    Book / Enquire
                </a>
                <a
                    href="#cattery"
                    data-testid="hero-explore-button"
                    className="rounded-full border border-stone-500/60 bg-cream/60 px-8 py-4 text-base font-semibold text-[#1c1917] backdrop-blur-sm transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#1c1917] hover:bg-cream sm:text-lg"
                >
                    Explore the cattery
                </a>
            </motion.div>

            <motion.div
                className="mt-14 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[11px] uppercase tracking-[0.22em] text-stone-600 sm:text-xs"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.35, duration: 0.9 }}
                data-testid="hero-facts"
            >
                <span>{pricing.currency}{pricing.standardRate} / cat / day</span>
                <span>Seven days a week</span>
            </motion.div>
        </div>

        <motion.a
            href="#story"
            aria-label="Scroll to our story"
            data-testid="hero-scroll-cue"
            className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-stone-500 transition-colors duration-300 hover:text-[#1c1917]"
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        >
            <ChevronDown className="h-7 w-7" />
        </motion.a>
    </section>
);
